import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { v4 } from 'uuid';
import Checkbox from '@prism/checkbox';
import Chip, { ChipColors } from '@prism/chip';
import { InteractiveInput } from '@prism/input';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var SearchableContext = /*#__PURE__*/React.createContext();

var _excluded$1 = ["data", "selectAll", "heading", "icon", "onChange", "disabled", "chipColor", "deSelectAll"];

function SearchableList(_ref) {
  var data = _ref.data,
      selectAll = _ref.selectAll,
      heading = _ref.heading,
      icon = _ref.icon,
      onChange = _ref.onChange,
      disabled = _ref.disabled,
      chipColor = _ref.chipColor;
      _ref.deSelectAll;
      var attributes = _objectWithoutProperties(_ref, _excluded$1);

  var _React$useContext = React.useContext(SearchableContext),
      searchValue = _React$useContext.searchValue;

  var _React$useState = React.useState(function () {
    return data.map(function (i, idx) {
      return _objectSpread2(_objectSpread2({}, i), {}, {
        // add indeces at first render
        idx: idx
      });
    });
  }),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      items = _React$useState2[0],
      setItems = _React$useState2[1];

  function withIndex(arr) {
    return arr.map(function (i, idx) {
      return _objectSpread2(_objectSpread2({}, i), {}, {
        idx: idx
      });
    });
  }

  React.useEffect(function () {
    // Check that we have data, so we don't try to iterate through undefined.
    if (!data) return null; // If data is updated by the parent, add indeces.

    setItems(withIndex(data));
  }, [data]); // Handle Checkbox interaction, returns both the updated data item
  // and the updated list to the parent component via callback.

  var handleCheckChange = function handleCheckChange(e, idx) {
    // If there's no item w/ matching index, return item, otherwise toggle item's checked property
    var newList = items.map(function (l, i) {
      return i === idx ? _objectSpread2(_objectSpread2({}, l), {}, {
        checked: !l.checked
      }) : l;
    }); // Update the list. Without this, focus changes on the search will revert items to initial state

    setItems(newList); // OUT - send new list state to parent

    onChange(e, newList);
  }; // provide filtered values as const so they persist even when hidden


  var filteredItems = React.useMemo(function () {
    return items.filter(function (item) {
      var sV = searchValue.toLowerCase();
      return item.label.toLowerCase().indexOf(sV) > -1 || item.checked;
    });
  }, [items, searchValue]);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: "searchable-list__container",
    role: "list"
  }, attributes), /*#__PURE__*/React.createElement("div", {
    className: "searchable-list__heading"
  }, icon && !searchValue.length > 0 && /*#__PURE__*/React.createElement("span", {
    className: "searchable-list__heading-icon icon prism-icon-".concat(icon)
  }), heading && !searchValue.length > 0 && heading), /*#__PURE__*/React.createElement("div", {
    className: "check-list"
  }, filteredItems && filteredItems.map(function (item) {
    return /*#__PURE__*/React.createElement("div", {
      key: v4(),
      className: "check-list__item",
      role: "listitem"
    }, /*#__PURE__*/React.createElement(Checkbox, _extends({
      disabled: disabled || item.disabled,
      defaultChecked: item.checked || selectAll,
      onChange: function onChange(e) {
        return handleCheckChange(e, item.idx);
      },
      className: "check-list__item",
      role: "checkbox"
    }, item)), item.count && item.count > 0 && /*#__PURE__*/React.createElement(Chip, {
      color: chipColor,
      className: "filter-count"
    }, item.count));
  })));
}

SearchableList.propTypes = {
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  onChange: PropTypes.func,
  heading: PropTypes.string,
  icon: PropTypes.string,
  selectAll: PropTypes.bool,
  deSelectAll: PropTypes.bool,
  disabled: PropTypes.bool,
  chipColor: PropTypes.oneOf(ChipColors)
};
SearchableList.defaultProps = {
  onChange: function onChange() {},
  data: [],
  heading: null,
  icon: null,
  selectAll: null,
  deSelectAll: null,
  disabled: false,
  chipColor: ChipColors[1] // "medium"

};

var _excluded = ["onChange", "onClear"];
var propTypes = {
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  disabled: PropTypes.bool
};
var defaultProps = {
  onChange: function onChange() {},
  onClear: function onClear() {},
  disabled: false
};

function SearchableSearch(_ref) {
  var props = _extends({}, _ref);

  var onChange = props.onChange,
      onClear = props.onClear,
      attributes = _objectWithoutProperties(props, _excluded);

  var _React$useContext = React.useContext(SearchableContext),
      searchValue = _React$useContext.searchValue;
      _React$useContext.searchClass;
      var handleClear = _React$useContext.handleClear,
      handleSearchChange = _React$useContext.handleSearchChange,
      handleFocus = _React$useContext.handleFocus;

  var handleOnChange = function handleOnChange(e) {
    handleSearchChange(e);
    onChange(e);
  };

  var handleClickRight = function handleClickRight(e) {
    handleClear(e);
    onClear(e);
  };

  return /*#__PURE__*/React.createElement("div", {
    className: "searchable-search ",
    role: "searchbox"
  }, /*#__PURE__*/React.createElement(InteractiveInput, _extends({
    placeholder: "Search the list below",
    className: "searchable-search__input",
    onClickRight: handleClickRight,
    onChange: handleOnChange,
    onFocus: handleFocus,
    iconRight: searchValue.length > 0 ? 'icon prism-icon-cancel-circle' : 'icon prism-icon-search',
    value: searchValue
  }, attributes)));
}

SearchableSearch.propTypes = propTypes;
SearchableSearch.defaultProps = defaultProps;

function Searchable(_ref) {
  var children = _ref.children;

  var _useState = useState({
    value: '',
    isSearching: false
  }),
      _useState2 = _slicedToArray(_useState, 2),
      search = _useState2[0],
      setSearch = _useState2[1];

  var setIsSearching = function setIsSearching(s) {
    return setSearch(function (val) {
      return _objectSpread2(_objectSpread2({}, val), {}, {
        isSearching: s
      });
    });
  };

  var setSearchValue = function setSearchValue(v) {
    return setSearch(function (sta) {
      return _objectSpread2(_objectSpread2({}, sta), {}, {
        value: v
      });
    });
  };

  useEffect(function () {
    setIsSearching(search.value !== '');
  }, [search.value]);
  var searchClass = classnames(search.isSearching && 'icon prism-icon-cancel-circle searchable-search__input-clear');

  var getInputProps = function getInputProps() {
    return {
      searchClass: searchClass,
      value: search.value,
      handleBlur: function handleBlur() {
        return setIsSearching(false);
      },
      handleFocus: function handleFocus() {
        return setIsSearching(true);
      },
      handleClear: function handleClear() {
        return setSearchValue('');
      },
      handleSearchChange: function handleSearchChange(e) {
        setSearchValue(e.target.value);
      }
    };
  };

  var ctx = _objectSpread2({
    search: search,
    setSearch: setSearch,
    isSearching: search.isSearching,
    searchValue: search.value,
    inputProps: getInputProps()
  }, getInputProps());

  return /*#__PURE__*/React.createElement(SearchableContext.Provider, {
    value: ctx
  }, children);
}

Searchable.propTypes = {
  children: PropTypes.node
};
Searchable.defaultProps = {
  children: null
};
Searchable.List = SearchableList;
Searchable.Search = SearchableSearch;

var SearchableProvider = SearchableContext.Provider;
Searchable.Context = SearchableContext;
Searchable.Provider = SearchableContext.Provider;
Searchable.List = SearchableList;
Searchable.Search = SearchableSearch;

export { SearchableContext, SearchableList, SearchableProvider, SearchableSearch, Searchable as default };
//# sourceMappingURL=index.js.map
